import React, { useState } from 'react';
import BaseFormGroup from 'components/atoms/base-form/group';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import BaseGrid from 'components/atoms/base-grid';
import FadeAnimation from 'components/atoms/base-animations/fade';
import { useGetInvoiceInfo } from 'infrastructure/hooks/clinic-invoices/use-get-invoice-info';
import { useGetClinicInvoices } from 'infrastructure/hooks/clinic-invoices/use-get-clinic-invoices';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { useUpdateClinicInvoicesInfo } from 'infrastructure/hooks/clinic-invoices/use-update-clinic-invoices-info';
import { useInvalidateQuery } from 'infrastructure/hooks/utils/use-invalidate-query';
import { ApiUrls } from 'infrastructure/consts/api-urls';

import ClinicInvoicesTable from './invoices-table';
import ClinicInvoicesInfoForm from './info-form';
import s from './styles.module.scss';

import type { IClinicInvoiceInfo } from 'infrastructure/interfaces';

interface IControlsProps {
  isEdit: boolean;
  isDisabled: boolean;
  onEdit: () => void;
  onCancel: () => void;
}

const Controls: React.FC<IControlsProps> = (props) => {
  const { isEdit, isDisabled, onEdit, onCancel } = props;
  const { t } = useTranslation();
  return (
    <FadeAnimation uniqueKey={`${isEdit}`}>
      {isEdit ? (
        <div className={s.controls}>
          <BaseButton
            dataCy="cancelInvoice-button"
            width="160px"
            type="secondary"
            onClick={onCancel}
            label={t('controls.cancel')}
            disabled={isDisabled}
          />
          <span id="invoice-form-control" />
        </div>
      ) : (
        <BaseButton
          dataCy="editInvoice-button"
          width="160px"
          onClick={onEdit}
          label={t('controls.edit')}
          disabled={isDisabled}
        />
      )}
    </FadeAnimation>
  );
};

interface IProps {
  clinicGuid: string;
  canEdit?: boolean;
}

const ClinicInvoicesForm: React.FC<IProps> = ({ clinicGuid, canEdit }) => {
  const { t } = useTranslation();

  const {
    data,
    loading: infoLoading,
    refetch: refetchInfo,
  } = useGetInvoiceInfo({ clinicGuid });

  const { items, refetch: refetchInvoices } = useGetClinicInvoices({
    clinicGuid,
  });

  const { refreshQuery } = useInvalidateQuery();

  const { mutateAsync, loading: updateLoading } = useUpdateClinicInvoicesInfo();

  const loading = infoLoading || updateLoading;

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const onEdit = () => {
    setIsEdit(true);
    setIsDisabled(true);

    setTimeout(() => {
      setIsDisabled(false);
    }, 500);
  };

  const onCancel = () => {
    for (const item of items) {
      item.reset();
    }
    setIsEdit(false);
  };

  const onSave = (val: IClinicInvoiceInfo) => {
    mutateAsync(
      {
        ...val,
        invoices: items,
      },
      {
        onSuccess: () => {
          refetchInfo().then(() => {
            setIsEdit(false);
          });
          refetchInvoices();
          refreshQuery({
            queryKey: [ApiUrls.clinicLogsGetApi(clinicGuid)],
            exact: false,
          });
        },
      },
    );
  };

  return (
    <BaseFormGroup
      label={t('labels.invoiceDetails')}
      controls={
        canEdit && (
          <Controls
            isEdit={isEdit}
            isDisabled={isDisabled}
            onEdit={onEdit}
            onCancel={onCancel}
          />
        )
      }
    >
      <BaseSpinWrapper spinning={loading}>
        <BaseGrid rowGap={30}>
          <ClinicInvoicesInfoForm
            isEdit={isEdit}
            isDisabled={isDisabled}
            data={data}
            onSave={onSave}
          />
          <ClinicInvoicesTable clinicGuid={clinicGuid} isEdit={isEdit} />
        </BaseGrid>
      </BaseSpinWrapper>
    </BaseFormGroup>
  );
};

export default ClinicInvoicesForm;
